import { zodResolver } from '@hookform/resolvers/zod'
import { useQueryState } from 'nuqs'
import { FieldErrors, FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import useSWR from 'swr'

import { ParameterChecklist } from '@/components/Catalog/ParameterChecklist'
import { VariantAxisChecklist } from '@/components/Catalog/VariantAxisChecklist'
import { VariantsInfoAlert } from '@/components/Catalog/VariantsInfoAlert'
import { Button } from '@/components/ui/button'
import { FormField, FormItem, FormLabel } from '@/components/ui/form'
import { apiFetcher } from '@/data/fetcher'
import { Category } from '@/interfaces/catalog/Category'
import API from '@/lib/api'
import { extractErrorMessage } from '@/lib/api/errorUtils'
import { saveProductAxes, saveProductParameters } from '@/lib/catalog/productStructure'
import { components } from '@invo.space/shared'

import { FormSection } from '../components/FormSection'
import { ProductFormBasicLayout, ProductFormCategoryFields, ProductFormStatusFields } from './ProductFormBasicLayout'
import { ProductFormSchema, ProductFormValues } from './schema'

export type ProductFormProps = {
  id?: string
  defaultValues?: Partial<ProductFormValues>
  onSuccess?: (data: components['schemas']['Product']) => void
  onCancel?: () => void
}

export const ProductForm = ({
  id,
  defaultValues,
  onSuccess,
  onCancel,
}: ProductFormProps) => {
  const { t } = useTranslation(undefined, {
    keyPrefix: 'catalog.products',
  })

  const { data: categoriesResponse } = useSWR<{ data?: Category[]; total?: number }>(
    '/api/v2/categories',
    apiFetcher,
  )

  const [categoryId] = useQueryState('categoryId')

  const categories = categoriesResponse?.data || []

  const methods = useForm<ProductFormValues>({
    resolver: zodResolver(ProductFormSchema) as any,
    defaultValues: {
      name: '',
      mainCategoryId: categoryId || '',
      description: null,
      secondaryCategoryIds: [],
      isActive: true,
      productType: 'physical' as const,
      packageItems: [],
      variantAxisIds: [],
      parameters: [],
      ...defaultValues,
    },
  })

  const onValid = async (values: ProductFormValues) => {
    try {
      const payload: Record<string, unknown> = {
        name: values.name,
        mainCategoryId: values.mainCategoryId,
        description: values.description || undefined,
        secondaryCategoryIds: values.secondaryCategoryIds?.length
          ? values.secondaryCategoryIds
          : undefined,
        isActive: values.isActive ?? true,
        productType: values.productType || undefined,
        ...(values.productType === 'package' && values.packageItems?.length
          ? { packageItems: values.packageItems }
          : {}),
      }

      if (id) {
        const { data } = await API.patch<components['schemas']['Product']>(
          `/api/v2/products/${id}`,
          {
            ...payload,
            productType: undefined, // remove for patch request
          },
        )
        toast.success(t('edit.success'))
        onSuccess?.(data)
      } else {
        const { data } = await API.post<components['schemas']['Product']>(
          '/api/v2/products',
          payload as components['schemas']['ProductCreate'],
        )
        toast.success(t('new.success'))
        await saveStructure(data.id, values)
        onSuccess?.(data)
      }
    } catch (error: unknown) {
      console.error('Product form error:', error)

      const apiError = error as {
        response?: { status?: number; data?: { detail?: string } }
      }

      if (apiError.response?.status === 409) {
        toast.error(t('error.conflict'), {
          description: apiError.response.data?.detail || t('error.description'),
        })
        return
      }

      const errorMessage = extractErrorMessage(error)
      toast.error(id ? t('edit.error') : t('new.error'), {
        description: errorMessage || t('error.description'),
      })
    }
  }

  // The product exists at this point, so a failure here must not block the redirect to it.
  const saveStructure = async (productId: string, values: ProductFormValues) => {
    try {
      if (values.variantAxisIds?.length) {
        await saveProductAxes(productId, values.variantAxisIds)
      }
      if (values.parameters?.length) {
        await saveProductParameters(productId, values.parameters)
      }
    } catch (error: unknown) {
      console.error('Product structure error:', error)
      toast.error(t('form.structure.error'), {
        description: extractErrorMessage(error) || t('error.description'),
      })
    }
  }

  const onInvalid = (errors: FieldErrors<ProductFormValues>) => {
    console.log('Form errors', errors, methods.getValues())
  }

  return (
    <FormProvider {...methods}>
      <form
        onSubmit={methods.handleSubmit(onValid, onInvalid)}
        className="grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_320px]"
      >
        <div className="grid min-w-0 gap-6">
          <FormSection sectionTitle="Základní údaje">
            <ProductFormBasicLayout productId={id} />
          </FormSection>

          {/* Existing products edit their variants and parameters in dialogs on the detail page */}
          {!id && (
            <FormSection sectionTitle={t('form.structure.title')}>
              <div className="space-y-4">
                <VariantsInfoAlert />
                <div className="grid gap-6 md:grid-cols-2">
                  <FormField
                    control={methods.control}
                    name="variantAxisIds"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('form.structure.variants')}</FormLabel>
                        <VariantAxisChecklist value={field.value ?? []} onChange={field.onChange} />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={methods.control}
                    name="parameters"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('form.structure.parameters')}</FormLabel>
                        <ParameterChecklist value={field.value ?? []} onChange={field.onChange} />
                      </FormItem>
                    )}
                  />
                </div>
              </div>
            </FormSection>
          )}
        </div>

        <aside className="grid gap-6">
          <FormSection sectionTitle="Stav">
            <ProductFormStatusFields />
          </FormSection>
          <FormSection sectionTitle="Kategorie">
            <ProductFormCategoryFields categories={categories} />
          </FormSection>
        </aside>

        <div className="flex justify-end gap-2 border-t pt-4 lg:col-span-2">
          {onCancel && (
            <Button type="button" variant="ghost" onClick={onCancel}>
              Zrušit
            </Button>
          )}
          <Button type="submit" disabled={methods.formState.isSubmitting}>
            {t('form.save')}
          </Button>
        </div>
      </form>
    </FormProvider>
  )
}
